import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { Component } from '@theme/component';

/**
 * @typedef {Object} ProductPriceRefs
 * @property {HTMLElement} priceContainer
 * @property {HTMLElement} [volumePricingNote]
 */

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 *
 * @extends {Component<ProductPriceRefs>}
 */
class ProductPrice extends Component {
  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    closestSection.addEventListener(ThemeEvents.variantSelected, this.updatePriceImmediately);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    closestSection.removeEventListener(ThemeEvents.variantSelected, this.updatePriceImmediately);
  }

  /**
   * Updates the price and volume pricing note.
   * @param {VariantUpdateEvent} event - The variant update event.
   */

  updatePriceImmediately = (event) => {
    const picker = event.target;
    if (!(picker instanceof HTMLElement) || picker.dataset.productId !== this.dataset.productId) return;
    const inputs = Array.from(picker.querySelectorAll('fieldset input:checked, select'));
    if (inputs.some((input) => {
      const option = input instanceof HTMLSelectElement ? input.selectedOptions[0] : input;
      return option?.dataset.connectedProductUrl && option.dataset.connectedProductUrl !== picker.dataset.productUrl;
    })) return;
    const selected = inputs.map((input, index) => ({
      index: Number(input.dataset.fieldsetIndex ?? index),
      value: input.value,
    }));
    if (!selected.length) return;
    const cached = Array.from(this.querySelectorAll('template[data-instant-variant-price]')).find((template) => {
      const options = JSON.parse(template.dataset.variantOptions || '[]');
      return selected.every((option) => options[option.index] === option.value);
    });
    if (!(cached instanceof HTMLTemplateElement)) return;
    const container = this.querySelector('[ref="priceContainer"]');
    const next = cached.content.querySelector('[ref="priceContainer"]');
    if (container && next) {
      container.replaceWith(next.cloneNode(true));
      const sticky = this.closest('.shopify-section')?.querySelector(
        `sticky-add-to-cart[data-product-id="${this.dataset.productId}"] [ref="priceContainer"]`
      );
      sticky?.replaceWith(next.cloneNode(true));
    }
    const note = this.querySelector('[ref="volumePricingNote"]');
    const nextNote = cached.content.querySelector('[ref="volumePricingNote"]');
    if (note && nextNote) note.replaceWith(nextNote.cloneNode(true));
    else if (note) note.remove();
    else if (nextNote) this.querySelector('[ref="priceContainer"]')?.after(nextNote.cloneNode(true));
  };

  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const { priceContainer, volumePricingNote } = this.refs;
    // Find the new product-price element in the updated HTML
    const newProductPrice = event.detail.data.html.querySelector(
      `product-price[data-block-id="${this.dataset.blockId}"]`
    );
    if (!newProductPrice) return;

    // Update price container
    const newPrice = newProductPrice.querySelector('[ref="priceContainer"]');
    if (newPrice && priceContainer) {
      priceContainer.replaceWith(newPrice);
    }

    // Update volume pricing note
    const newNote = newProductPrice.querySelector('[ref="volumePricingNote"]');

    if (!newNote) {
      volumePricingNote?.remove();
    } else if (!volumePricingNote) {
      // Use newPrice since priceContainer was just replaced and now points to the detached element
      newPrice?.insertAdjacentElement('afterend', /** @type {Element} */ (newNote.cloneNode(true)));
    } else {
      volumePricingNote.replaceWith(newNote);
    }

    // Update installments (SPI banner) variant ID to trigger payment terms re-render
    const input_selector = `#product-form-installment-${this.dataset.blockId} input[name="id"]`;
    const installmentsInput = /** @type {HTMLInputElement|null} */ (this.querySelector(input_selector));
    if (installmentsInput) {
      installmentsInput.value = event.detail.resource?.id ?? '';
      installmentsInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  };
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
